import { getFunctions, httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebaseInit';
import { getEnvironment } from '../config/firebase';

/**
 * Azure Blob Storage Service for Fitzaa Photo Management
 * 
 * This service uses Firebase Functions as a secure proxy to Azure Blob Storage
 * to avoid exposing credentials in the frontend and resolve Azure SDK compatibility issues.
 * 
 * Features:
 * - Upload photos to hot tier (fast access)
 * - Automatic lifecycle management (moves to cool/archive tiers)
 * - CDN integration for fast delivery
 * - Cost optimization with tiered storage
 * - Secure credential handling via Firebase Functions
 */
export class AzureBlobStorageService {
    private static instance: AzureBlobStorageService;
    private functionsInstance = functions;
    private isConfigured: boolean = false;

    private constructor() {
        // Service is always configured when using Firebase Functions
        this.isConfigured = true;
    }

    /**
     * Get singleton instance
     */
    static getInstance(): AzureBlobStorageService {
        if (!AzureBlobStorageService.instance) {
            AzureBlobStorageService.instance = new AzureBlobStorageService();
        }
        return AzureBlobStorageService.instance;
    }

    /**
     * Check if service is configured
     */
    isServiceConfigured(): boolean {
        return this.isConfigured;
    }

    /**
     * Upload photo to Azure Blob Storage via Firebase Functions
     */
    async uploadPhoto(photoData: string, fileName: string, contentType: string): Promise<{ blobName: string; url: string }> {
        try {
            if (!this.isConfigured) {
                throw new Error('Service not configured');
            }

            const uploadPhotoFunction = httpsCallable(this.functionsInstance, 'uploadPhoto');
            const result = await uploadPhotoFunction({
                photoData,
                fileName,
                contentType,
                environment: getEnvironment()
            });

            const response = result.data as any;
            if (response.success) {
                return {
                    blobName: response.blobName,
                    url: response.url
                };
            } else {
                throw new Error(response.message || 'Upload failed');
            }
        } catch (error) {
            console.error('[AzureBlobStorageService] Upload failed:', error);
            throw error;
        }
    }

    /**
     * Get secure access URL for a photo via Firebase Functions
     */
    async getPhotoUrl(blobName: string): Promise<{ sasUrl: string; expiresAt: number }> {
        try {
            if (!this.isConfigured) {
                throw new Error('Service not configured');
            }

            const getPhotoFunction = httpsCallable(this.functionsInstance, 'getPhotoFromAzure');
            const result = await getPhotoFunction({ 
                blobName,
                environment: getEnvironment()
            });

            const response = result.data as any;
            if (response.success) {
                return {
                    sasUrl: response.sasUrl,
                    expiresAt: response.expiresAt
                };
            } else {
                throw new Error(response.message || 'Failed to get photo URL');
            }
        } catch (error) {
            console.error('[AzureBlobStorageService] Get photo URL failed:', error);
            throw error;
        }
    }

    /**
     * List blobs in a container (placeholder for future implementation)
     */
    async listBlobs(containerName: string, prefix?: string): Promise<string[]> {
        // TODO: Implement if needed via Firebase Functions
        throw new Error('List blobs not implemented yet');
    }

    /**
     * Get blob properties (placeholder for future implementation)
     */
    async getBlobProperties(blobName: string): Promise<any> {
        // TODO: Implement if needed via Firebase Functions
        throw new Error('Get blob properties not implemented yet');
    }

    /**
     * Get blob download URL (placeholder for future implementation)
     */
    async getBlobDownloadUrl(blobName: string): Promise<string> {
        // TODO: Implement if needed via Firebase Functions
        throw new Error('Get blob download URL not implemented yet');
    }

    /**
     * Delete photo from Azure Blob Storage via Firebase Functions
     */
    async deletePhoto(blobName: string): Promise<void> {
        try {
            if (!this.isConfigured) {
                throw new Error('Service not configured');
            }

            console.log(`[AzureBlobStorageService] 🗑️ Attempting to delete blob: ${blobName}`);
            const deletePhotoFunction = httpsCallable(this.functionsInstance, 'deletePhotoFromAzure');
            const result = await deletePhotoFunction({ 
                blobName,
                environment: getEnvironment()
            });

            const response = result.data as any;
            if (response.success) {
                console.log(`[AzureBlobStorageService] ✅ Photo deleted successfully: ${blobName}`);
            } else {
                console.error(`[AzureBlobStorageService] ❌ Delete failed for ${blobName}:`, response.message);
                throw new Error(response.message || 'Delete failed');
            }
        } catch (error) {
            console.error(`[AzureBlobStorageService] ❌ Delete failed for ${blobName}:`, error);
            throw error;
        }
    }
}
